<template>
  <div class="task-list">
    <!-- 加载状态 -->
    <div v-if="loading" class="task-loading">
      <el-loading-spinner></el-loading-spinner>
      <p>加载中…</p>
    </div>

    <!-- 任务卡片列表 -->
    <el-row v-else :gutter="16">
      <el-col
        v-for="(item, i) in taskList"
        :key="item.id || i"
        :xs="24" :sm="12" :md="8" :lg="4"
        class="task-col"
      >
        <el-card
          :body-style="{ padding: '0px' }"
          :class="['task-card', { 'is-taken': isTaken(item) }]"
        >
          <!-- 封面图 -->
          <el-image
            :src="item.pic_url"
            fit="cover"
            class="task-cover"
            lazy
          >
            <div slot="error" class="task-cover-error">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>

          <!-- 卡片内容 -->
          <div class="task-body">
            <p class="task-title">{{ item.title }}</p>
            <ul class="task-meta">
              <li>
                <span class="label">分组</span>
                <span class="value">{{ taskGroup(item) }}</span>
              </li>
              <li>
                <span class="label">难度</span>
                <span class="value">
                  <el-rate
                    :value="normalizeHard(item.hard)"
                    :max="5"
                    disabled
                  ></el-rate>
                </span>
              </li>
              <li><span class="label">话数</span><span class="value">第 {{ item.HuaSu }} 话 / 共 {{ item.P }} P</span></li>
              <li><span class="label">交稿</span><span class="value">{{ item.DDL }} 天后</span></li>
              <li><span class="label">发布者</span><span class="value">{{ item.helper }}</span></li>
              <li><span class="label">发布时间</span><span class="value">{{ item.time }}</span></li>
            </ul>

            <!-- 已认领标签 -->
            <el-tag v-if="isTaken(item)" type="info" size="small" style="margin-top:8px">已认领</el-tag>

            <!-- 接单按钮 -->
            <div v-if="!isTaken(item)" class="task-action">
              <el-button
                type="primary"
                size="small"
                plain
                @click="onAccept(item)"
              >接下任务</el-button>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 接单确认弹窗 -->
    <el-dialog
      title="确认接下任务"
      :visible.sync="dialogVisible"
      width="400px"
      custom-class="accept-dialog"
      :before-close="onDialogClose"
    >
      <p>您选择接下：<strong>{{ currentTask && currentTask.title }}</strong></p>
      <p style="color:#999; font-size:13px">接单后请联系任务发布者领取图源和译文，感谢您的帮助！</p>
      <div class="task-detail-box">
        <div class="task-detail-line">
          <span class="detail-label">发布者署名</span>
          <span class="detail-value">{{ currentTask ? currentTask.helper : '-' }}</span>
        </div>
        <div class="task-detail-line">
          <span class="detail-label">发布者 QQ</span>
          <span class="detail-value">{{ taskQQ(currentTask) }}</span>
        </div>
      </div>
      <el-form label-width="80px" style="margin-top:16px">
        <el-form-item label="你的组内署名">
          <el-input
            v-model="inputUser"
            placeholder="例如：冬至"
            clearable
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" :disabled="!inputUser.trim()" @click="submitAccept">确认接单</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getZmbList, takeZmbTask, getFyList, takeFyTask } from '@/api/index'

export default {
  name: 'TaskList',

  /**
   * type: 'zmb' | 'fy'
   *   zmb → 嵌字任务
   *   fy  → 翻译任务
   */
  props: {
    type: {
      type: String,
      default: 'zmb',
      validator: v => ['zmb', 'fy'].includes(v)
    }
  },

  data() {
    return {
      loading: false,
      taskList: [],

      dialogVisible: false,
      currentTask: null,
      inputUser: '',
      submitting: false
    }
  },

  created() {
    this.fetchList()
  },

  methods: {
    /** 获取任务列表 */
    async fetchList() {
      this.loading = true
      try {
        const res = this.type === 'zmb'
          ? await getZmbList()
          : await getFyList()
        this.taskList = res.data || []
      } catch (e) {
        this.$message.error('获取任务列表失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    /** 判断任务是否已认领（标题首字母为 Q） */
    isTaken(item) {
      return item.title && item.title[0] === 'Q'
    },

    normalizeHard(value) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return Math.min(5, Math.max(0, parsed))
      }
      return 0
    },

    /** 解析 note 字段：兼容 JSON 或旧格式 */
    parseNote(note) {
      const empty = { group: '', qq: '' }

      if (!note) {
        return empty
      }

      if (typeof note === 'object') {
        return {
          group: String(note.group || note.groupName || note.g || '').trim(),
          qq: String(note.qq || note.QQ || note.qqNumber || '').trim()
        }
      }

      const text = String(note).trim()
      if (!text) {
        return empty
      }

      try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed === 'object') {
          return {
            group: String(parsed.group || parsed.groupName || parsed.g || '').trim(),
            qq: String(parsed.qq || parsed.QQ || parsed.qqNumber || '').trim()
          }
        }
      } catch (e) {
        // fall through to legacy formats
      }

      const legacySplit = text.split('|||')
      if (legacySplit.length >= 2) {
        return {
          group: String(legacySplit[0] || '').trim(),
          qq: String(legacySplit.slice(1).join('|||') || '').trim()
        }
      }

      const pipeSplit = text.split('|')
      if (pipeSplit.length >= 2) {
        return {
          group: String(pipeSplit[0] || '').trim(),
          qq: String(pipeSplit.slice(1).join('|') || '').trim()
        }
      }

      return {
        group: text,
        qq: ''
      }
    },

    taskGroup(item) {
      const note = this.parseNote(item && item.note)
      return note.group || (item && item.group) || '-'
    },

    taskQQ(item) {
      const note = this.parseNote(item && item.note)
      return note.qq || (item && item.qq) || '-'
    },

    /** 点击接单按钮 */
    onAccept(item) {
      this.currentTask = item
      this.inputUser = ''
      this.dialogVisible = true
    },

    onDialogClose(done) {
      this.$confirm('确认关闭？').then(() => done()).catch(() => {})
    },

    /** 提交接单 */
    async submitAccept() {
      if (!this.inputUser.trim()) {
        this.$message.warning('请输入您的组内署名')
        return
      }
      this.submitting = true
      try {
        const fn = this.type === 'zmb' ? takeZmbTask : takeFyTask
        await fn(this.currentTask.id, this.inputUser.trim())
        this.$message.success('接单成功！请联系发布者领取素材')
        this.dialogVisible = false
        await this.fetchList()   // 刷新列表，无需整页 reload
      } catch (e) {
        this.$message.error('接单失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.task-list {
  padding: 8px;
}

.task-col {
  margin-bottom: 16px;
}

.task-card {
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.task-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.task-card.is-taken {
  opacity: 0.35;
  pointer-events: none;
}

.task-cover {
  width: 100%;
  height: auto;
  aspect-ratio: 2.8 / 3;
  display: block;
}

.task-cover-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #c0c4cc;
  font-size: 32px;
}

.task-body {
  padding: 12px;
}

.task-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-meta {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: #666;
}

.task-meta li {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  gap: 6px;
}

.task-meta .label {
  color: #999;
  flex-shrink: 0;
  margin-right: 4px;
}

.task-meta .value {
  text-align: right;
  word-break: break-all;
}

.task-action {
  margin-top: 10px;
  text-align: right;
}

.task-detail-box {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f9fbff;
  border: 1px solid #ebeef5;
}

.task-detail-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
}

.detail-label {
  color: #909399;
  flex-shrink: 0;
}

.detail-value {
  text-align: right;
  word-break: break-all;
}

.task-loading {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

@media (max-width: 768px) {
  .task-list {
    padding: 4px 0;
  }

  .task-title {
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    word-break: break-word;
  }

  .task-meta li {
    align-items: flex-start;
  }

  .task-meta .value {
    max-width: 68%;
  }

  .task-action,
  .task-action .el-button {
    width: 100%;
    text-align: center;
  }

  .accept-dialog {
    width: 94% !important;
    margin-top: 0 !important;
  }

  .accept-dialog .el-dialog__body {
    padding: 16px 12px;
  }

  .accept-dialog .el-form-item__label,
  .accept-dialog .el-form-item__content {
    float: none;
    width: 100% !important;
    margin-left: 0 !important;
    text-align: left;
  }

  .task-detail-line {
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/TaskList.vue